import { EDITABLE_FORM_KEYS, PERSONAL_FIELDS, WORK_FIELDS } from '../constants/profileFields';

/**
 * Editing the profile: starting values, checks before saving, what to send, and reading the
 * server's complaints.
 */

const FIELDS_BY_KEY = Object.fromEntries(
    [...PERSONAL_FIELDS, ...WORK_FIELDS].filter((field) => field.formKey).map((field) => [field.formKey, field])
);

// Digits with an optional leading +, and spaces, dashes or brackets in between.
const PHONE_PATTERN = /^\+?[0-9\s\-()]{6,}$/;

const asText = (value) => (typeof value === 'string' ? value : '');

/** Starting values for the edit form, taken from the normalized profile. */
export const toFormValues = (profile) => ({
    first_name: asText(profile?.firstName),
    last_name: asText(profile?.lastName),
    designation: asText(profile?.designation),
    department: asText(profile?.department),
    location: asText(profile?.location),
    phone_number: asText(profile?.phoneNumber),
});

/** @returns {Object<string,string>} a message per field that is not valid (empty object = valid) */
export const validateProfileForm = (values) => {
    const errors = {};

    EDITABLE_FORM_KEYS.forEach((key) => {
        const field = FIELDS_BY_KEY[key];
        const value = asText(values[key]).trim();

        if (field.required && !value) {
            errors[key] = `${field.label} is required.`;
        } else if (value.length > field.maxLength) {
            errors[key] = `${field.label} must be ${field.maxLength} characters or fewer.`;
        }
    });

    const phone = asText(values.phone_number).trim();
    if (!errors.phone_number && phone && (!PHONE_PATTERN.test(phone) || phone.replace(/\D/g, '').length < 6)) {
        errors.phone_number = 'Enter a valid phone number, for example +971 50 123 4567.';
    }

    return errors;
};

/** The body of the PATCH request: every editable field, trimmed. */
export const buildUpdatePayload = (values) =>
    Object.fromEntries(EDITABLE_FORM_KEYS.map((key) => [key, asText(values[key]).trim()]));

/** True when any editable field differs from where it started. */
export const hasProfileChanges = (values, initial) =>
    EDITABLE_FORM_KEYS.some((key) => asText(values[key]).trim() !== asText(initial[key]).trim());

/**
 * Reads a failed save.
 *   422: complaints about specific fields (shown under those fields) or a general message
 *   403: not allowed to edit this profile
 * @returns {{ fieldErrors: Object<string,string>, message: string }}
 */
export const describeUpdateError = (error) => {
    if (!error?.response) {
        return { fieldErrors: {}, message: 'Cannot reach the server. Please check your connection and try again.' };
    }

    const { status, data } = error.response;
    const detail = data?.detail;

    if (status === 422 && Array.isArray(detail)) {
        const fieldErrors = {};
        let message = '';
        detail.forEach((item) => {
            const text = String(item?.msg || '').replace(/^value error,\s*/i, '').trim();
            const field = [...(item?.loc || [])].reverse().find((part) => EDITABLE_FORM_KEYS.includes(part));
            if (field && text && !fieldErrors[field]) fieldErrors[field] = text;
            else if (text && !message) message = text;
        });
        const hasFieldErrors = Object.keys(fieldErrors).length > 0;
        return {
            fieldErrors,
            message: hasFieldErrors ? 'Please fix the highlighted fields.' : message || 'Please check the details you entered.',
        };
    }

    const serverMessage = (typeof detail === 'string' && detail.trim()) || '';
    if (status === 403) return { fieldErrors: {}, message: serverMessage || 'You can only edit your own profile.' };
    if (status === 404) return { fieldErrors: {}, message: 'This profile could not be found.' };
    if (status === 429) return { fieldErrors: {}, message: 'Too many attempts. Please wait a moment and try again.' };
    return { fieldErrors: {}, message: serverMessage || 'Your profile could not be saved. Please try again.' };
};
